import { useCallback, useEffect, useRef, useState } from "react";
import { notification } from "antd";
import { getLatestReleaseInfo } from "./releaseVersion";
import { useScopeParams } from "@/hooks/useScopeParams";

const SEEN_VERSION_STORAGE_KEY = "_vivoReleaseVersion";

type ReleaseInfo = {
  slug: string;
  version: string;
};

type NotificationMessageProps = {
  buildUrl: (path?: string) => string;
  releaseInfo: ReleaseInfo;
};

const NotificationMessage = ({
  buildUrl,
  releaseInfo,
}: NotificationMessageProps) => (
  <div className="text-[#064c68]">
    <span>{`Version ${releaseInfo.version} is live with new updates! View the `}</span>
    <span
      className="text-[#064c68] cursor-pointer underline p-0 m-0 no-hover-bg"
      onClick={() =>
        window.open(
          buildUrl(`wiki/release-notes/${releaseInfo.slug}`),
          "_blank",
        )
      }
    >
      release notes
    </span>
    .
  </div>
);

export default function Announcements() {
  const [api, contextHolder] = notification.useNotification();
  const { buildUrl } = useScopeParams();
  const [releaseInfo, setReleaseInfo] = useState<ReleaseInfo | null>(null);

  // buildUrl is rebuilt whenever the route's scope/id changes. Reading it
  // through a ref keeps the announce effect from re-firing on navigation —
  // otherwise every route change inside the Home layout opened another toast.
  const buildUrlRef = useRef(buildUrl);
  buildUrlRef.current = buildUrl;

  // The version this mount has already announced, so React StrictMode's
  // double-invoked effects and any re-render cannot open a second toast.
  const announcedVersionRef = useRef<string | null>(null);

  const openNotification = useCallback(
    (info: ReleaseInfo) => {
      api.open({
        // A stable key makes a repeat open replace the existing toast instead
        // of stacking a new one on top of it.
        key: `vivo-release-${info.version}`,
        title: (
          <p className="text-[#064c68] text-base font-semibold">
            Update Alert!
          </p>
        ),
        description: (
          <NotificationMessage
            buildUrl={(path) => buildUrlRef.current(path)}
            releaseInfo={info}
          />
        ),
        placement: "bottom",
        className: "announcement",
        duration: false,
      });
    },
    [api],
  );

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      const info = await getLatestReleaseInfo();
      if (mounted && info) setReleaseInfo(info);
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!releaseInfo) return;
    if (announcedVersionRef.current === releaseInfo.version) return;

    const storedVersion = localStorage.getItem(SEEN_VERSION_STORAGE_KEY);
    if (storedVersion === releaseInfo.version) return;

    announcedVersionRef.current = releaseInfo.version;
    // Record the version as seen when the toast is shown rather than when it is
    // dismissed. Closing only ever dismissed one toast, so a release announced
    // itself again on every reload and every navigation until one was clicked.
    localStorage.setItem(SEEN_VERSION_STORAGE_KEY, releaseInfo.version);
    openNotification(releaseInfo);
  }, [openNotification, releaseInfo]);

  return <div>{contextHolder}</div>;
}
